import { bareInstance } from "../axios/axios";

// ITAM-2622 (3.7) — logout-time side effects only.
//
// BARE axios on purpose. The shared instance's response interceptor fires on
// ANY 401/403 and executes `window.location.href = "/auth"` — a hard navigation
// that unloads the document and CANCELS every in-flight request. On the shared
// instance the two dual-role side effects would therefore not be independent: a
// 403 from one could unload the page and kill the other, and a 403 is realistic
// here because the pause endpoint can legitimately reject (no support row,
// missing DB role). It also dispatches its own logout, clearing localStorage
// while the sibling request may still need the token.
//
// This is a deliberate, narrow exception to "no bare-axios moves", which
// governs CONSULT SERVICES, not logout side effects. It costs one thing: the
// bare instance must attach `Authorization` itself, which is why every function
// below takes a pre-captured token.

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });

export const pausePharmacistOnLogout = (
  userId: number,
  token: string,
  signal: AbortSignal
) =>
  bareInstance.put(
    `/pharmacist_profile/user_id/${userId}/availability?available=false`,
    null,
    { signal, headers: auth(token) }
  );

export const clearTakesOrdersOnLogout = (
  branchMarketplaceId: number,
  marketplaceId: number,
  token: string,
  signal: AbortSignal
) =>
  bareInstance.put(
    `/marketplace/takes_orders/${branchMarketplaceId}/${marketplaceId}?takesOrders=0`,
    null,
    { signal, headers: auth(token) }
  );

export const LOGOUT_SIDE_EFFECT_TIMEOUT_MS = 3000;

/**
 * One controller, one timer, shared by every task.
 *
 * The bound is for REQUEST HYGIENE, not for the session guarantee — the caller
 * clears the session without waiting for this at all. It is still a real bound
 * rather than a hope: `Promise.allSettled` is not a timeout (it settles only
 * when every input settles), so what makes the timer effective is the abort.
 * Axios honors `signal`, and an aborted request REJECTS with `CanceledError`,
 * which settles the input promise.
 */
export async function runBoundedLogoutSideEffects(
  tasks: Array<(signal: AbortSignal) => Promise<unknown>>
): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    LOGOUT_SIDE_EFFECT_TIMEOUT_MS
  );
  try {
    await Promise.allSettled(tasks.map((task) => task(controller.signal)));
  } finally {
    clearTimeout(timer);
  }
}
